import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ChartBottomBtn from 'components/pages/chart/ChartBottomBtn';
import ConfirmChartRegister from 'pages/modal/ConfirmChartRegister';
import { PreventiveDentalTreatmentChartFormDefaultValues } from 'constants/chart/preventiveDentalTreatmentChart';
import { TPreventiveDentalTreatmentChartFormValues } from 'types/chart/preventiveDentalTreatmentChart';
import { TPracticeChartInfo } from 'types/apis/chart/studentPracticeChart';
import {
  TEvaluationChartInfo,
  TPutEvaluationChartResponse,
} from 'types/apis/chart/studentEvaluationChart';
import useModal from 'hooks/useModal';
import { TChartInfoResponse } from 'hooks/chart/useGetChartInfo';
import useChartRegister from 'hooks/chart/useChartRegister';
import PreventiveDentalTreatmentChart from '../PreventiveDentalTreatmentChart';

type TProps = {
  query: URLSearchParams;
  chartType: number;
  category: string;
  id: number;
  isRegister: boolean;
  setSubmitEndDate: (submitEndDate: string) => void;
  chartInfoResponse: TChartInfoResponse | null;
  setChartInfoResponse: (data: any) => void;
  activeTabIndex: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isEdit: boolean;
  isReadOnly: boolean;
  checkSubmitEndDate: (submitEndDate: string) => void;
  handleSuccessGetChartInfo: (data: TChartInfoResponse) => void;
};

const PreventiveDentalTreatmentChartRegister = ({
  query,
  chartType,
  category,
  id,
  isRegister,
  setSubmitEndDate,
  chartInfoResponse,
  setChartInfoResponse,
  activeTabIndex,
  isLoading,
  setIsLoading,
  isEdit,
  isReadOnly,
  checkSubmitEndDate,
  handleSuccessGetChartInfo,
}: TProps) => {
  const { openModal } = useModal();

  const { chartRegister } = useChartRegister(({ data }) => afterAutoSave(data));

  const methods = useForm<TPreventiveDentalTreatmentChartFormValues>({
    defaultValues: PreventiveDentalTreatmentChartFormDefaultValues,
  });
  const { handleSubmit, setValue, watch, reset } = methods;
  const formValues = watch();
  const formValuesRef = useRef(formValues);
  const prevFormValuesRef =
    useRef<TPreventiveDentalTreatmentChartFormValues | null>(null);

  // 실습용 - 자동 저장 (20초 간격)
  useEffect(() => {
    if (isLoading) return;
    prevFormValuesRef.current = formValues;
  }, [isLoading]);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (!isLoading && isRegister && category === 'evaluation') {
      intervalId = setInterval(() => {
        if (isReadOnly) return;
        // 초기 상태 자동 저장 방지
        const chartInfoList = chartInfoResponse?.chartInfo as
          | TEvaluationChartInfo[]
          | undefined;
        if (
          chartInfoList &&
          chartInfoList.length > (activeTabIndex > 1 ? 1 : 0)
        ) {
          const currentChartInfo = chartInfoList[activeTabIndex > 1 ? 1 : 0];
          if (
            currentChartInfo?.chartData === null &&
            JSON.stringify(PreventiveDentalTreatmentChartFormDefaultValues) ===
              JSON.stringify(formValuesRef.current)
          ) {
            return;
          }
        }

        // 이전 값과 비교하여 변경된 경우만 자동 저장 요청
        if (
          JSON.stringify(formValuesRef.current) !==
          JSON.stringify(prevFormValuesRef.current)
        ) {
          onValid(true, formValuesRef.current);
        }
      }, 20000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRegister, isLoading, category, isReadOnly]);

  const afterAutoSave = ({ chartInfo }: TPutEvaluationChartResponse) => {
    setChartInfoResponse((prev: any) => {
      if (!prev) return prev;
      const newChartInfo = prev.chartInfo as TEvaluationChartInfo[];
      const newChartInfoData = [...newChartInfo];
      newChartInfoData[activeTabIndex > 1 ? 1 : 0] = {
        ...newChartInfoData[activeTabIndex > 1 ? 1 : 0],
        chartData: chartInfo?.chartData || null,
        fileInfoList: chartInfo?.fileInfoList || [],
      };
      return {
        ...prev,
        chartInfo: newChartInfoData,
      };
    });
    prevFormValuesRef.current = chartInfo?.chartData || null;
  };

  const setChartInfo = (
    chartInfo: TPracticeChartInfo | TEvaluationChartInfo
  ) => {
    let chartData: TPreventiveDentalTreatmentChartFormValues | null =
      (chartInfo?.chartData as TPreventiveDentalTreatmentChartFormValues | null) ||
      null;

    if (category !== 'practice') {
      const submitEndDate =
        (chartInfo as TEvaluationChartInfo)?.submitEndDate || '';
      setSubmitEndDate(submitEndDate);
      checkSubmitEndDate(submitEndDate);

      const prevData =
        (chartInfoResponse?.chartInfo as TEvaluationChartInfo[])[0]
          ?.chartData || null;
      if (prevData && activeTabIndex > 1) {
        const filteredData = Object.keys(prevData)
          .filter((key) => key.includes('1'))
          .reduce(
            (obj, key) => {
              (obj as Record<string, any>)[key] =
                prevData[key as keyof typeof prevData];
              return obj;
            },
            {} as Partial<typeof prevData>
          );
        if (!chartData) {
          chartData =
            PreventiveDentalTreatmentChartFormDefaultValues as TPreventiveDentalTreatmentChartFormValues;
        }
        Object.keys(filteredData).forEach((key) => {
          if (!chartData) return;
          chartData[key as keyof TPreventiveDentalTreatmentChartFormValues] =
            filteredData[key as keyof typeof filteredData];
        });
      }
    }
    if (!chartData) {
      reset();
      setIsLoading(false);
      return;
    }
    Object.keys(chartData).forEach((key) => {
      if (!chartData) return;
      setValue(
        key as keyof TPreventiveDentalTreatmentChartFormValues,
        chartData[key as keyof TPreventiveDentalTreatmentChartFormValues]
      );
    });
    setIsLoading(false);
  };

  useEffect(() => {
    if (chartInfoResponse) {
      handleSuccessGetChartInfo(chartInfoResponse);
    }
  }, []);

  // 차트 조회
  useEffect(() => {
    if (!chartInfoResponse) return;
    const chartInfo =
      category === 'practice'
        ? (chartInfoResponse.chartInfo as TPracticeChartInfo)
        : (chartInfoResponse.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ];
    setIsLoading(true);
    setChartInfo(chartInfo);
  }, [activeTabIndex, chartInfoResponse]);

  // 차트 등록
  const onValid = (
    isAutoSave: boolean,
    data: TPreventiveDentalTreatmentChartFormValues
  ) => {
    const formData = new FormData();

    formData.append(
      'request',
      new Blob(
        [
          JSON.stringify({
            chartType,
            chartData: data,
          }),
        ],
        {
          type: 'application/json',
        }
      )
    );
    chartRegister({
      category,
      id: id,
      data: formData,
      isEdit: isEdit,
      isAutoSave,
      patientId: query.get('patientId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
      onLoading: setIsLoading,
      onConfirm: () => {
        openModal({
          element: (
            <ConfirmChartRegister
              category={category}
              chartType={chartType}
              id={id}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              chartName={chartInfoResponse?.chartName}
              data={formData}
            />
          ),
        });
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((data) => onValid(false, data))}>
          <PreventiveDentalTreatmentChart
            category={category}
            isRegister={isRegister}
            isReadOnly={isReadOnly}
            activeTabIndex={activeTabIndex}
          />
          {(Number(query.get('tab')) === 0 ||
            Number(query.get('tab')) === 2) && (
            <ChartBottomBtn
              category={category}
              id={id}
              courseId={query.get('courseId') || undefined}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              studentUserId={query.get('studentUserId') || undefined}
              isRegister={isRegister}
              isEdit={isEdit}
              isReadOnly={isReadOnly}
              isChart={true}
              chartName={chartInfoResponse?.chartName}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default PreventiveDentalTreatmentChartRegister;
